import * as React from 'react';
import { DashboardLayoutComponent, PanelDirective, PanelsDirective } from '@syncfusion/ej2-react-layouts';
import '../analytics.css';
import { useAuth0 } from '@auth0/auth0-react';
import useAuthenticatedAxios from "../../../auth/AuthenticatedAxios";
import { useEffect, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';

// Import component files
import UpcomingSessions from './UpcomingSessions';
import AdviceStatusOverview from './AdviceStatusOverview';
import EarningsSummary from './EarningsSummary';
import EngagementReach from './EngagementReach';
import ClientInsights from './ClientInsights';
import PlatformNotifications from './PlatformNotifications';
import BlurredComponent from './BlurredComponent';
import ExpertReviews from './ExpertReviews';
import LazyPanel from './LazyPanel';

// Import Dashboard Assistant
import { DashboardAssistantProvider, DashboardAssistantPanel } from '../../DashboardAssistant';

// Import dashboard style constants
import { COLORS, DashboardWrapper } from './styles';

// Import assistant icon
import HubAssistantIcon from '../../../Images/hubAssistant.svg';
import CustomToolbarActions from '../../RightToolbar/CustomToolbarActions';

// ============================================
// ANIMATIONS
// ============================================

const slideIn = keyframes`
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const slideOut = keyframes`
  from { transform: translateX(0); opacity: 1; }
  to { transform: translateX(100%); opacity: 0; }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 8px rgba(99, 102, 241, 0.3); }
  50% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.6); }
`;

// ============================================
// STYLED COMPONENTS FOR ASSISTANT
// ============================================

const ToolbarContainer = styled.div`
  position: fixed;
  right: ${props => props.$isPanelOpen ? '400px' : '16px'};
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 998;
  transition: right 0.3s ease;
`;

const ToolbarButton = styled.button`
  position: relative;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  border: 1px solid ${props => props.$active 
    ? 'rgba(99, 102, 241, 0.5)' 
    : 'rgba(99, 102, 241, 0.2)'};
  background: ${props => props.$active 
    ? 'rgba(99, 102, 241, 0.15)' 
    : 'rgba(20, 25, 40, 0.95)'};
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  
  &:hover {
    border-color: rgba(99, 102, 241, 0.5);
    background: rgba(99, 102, 241, 0.1);
    transform: scale(1.05);
  }
  
  ${props => props.$active && css`
    animation: ${glow} 2s ease-in-out infinite;
  `}
  
  img {
    width: 28px;
    height: 28px;
    object-fit: contain;
  }
`;

const Tooltip = styled.div`
  position: absolute;
  right: calc(100% + 12px);
  top: 50%;
  transform: translateY(-50%);
  background: rgba(20, 25, 40, 0.98);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  
  ${ToolbarButton}:hover & {
    opacity: 1;
  }
`;

const AssistantPanel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 400px;
  height: 100vh;
  background: linear-gradient(180deg, #0f1629 0%, #0a0f1a 100%);
  border-left: 1px solid rgba(99, 102, 241, 0.2);
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: flex;
  flex-direction: column;
  
  ${props => props.$isOpen ? css`
    animation: ${slideIn} 0.3s ease forwards;
  ` : css`
    animation: ${slideOut} 0.3s ease forwards;
  `}
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.08) 100%);
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
  
  .title-section {
    display: flex;
    align-items: center;
    gap: 12px;
    
    .icon {
      width: 36px;
      height: 36px;
      border-radius: 10px;
      background: rgba(99, 102, 241, 0.1);
      border: 1px solid rgba(99, 102, 241, 0.2);
      display: flex;
      align-items: center;
      justify-content: center;
      
      img {
        width: 22px;
        height: 22px;
      }
    }
    
    .text {
      h3 {
        margin: 0;
        font-size: 15px;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.95);
      }
      
      span {
        font-size: 11px;
        color: rgba(165, 180, 252, 0.7);
      }
    }
  }
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(239, 68, 68, 0.2);
    border-color: rgba(239, 68, 68, 0.3);
    color: #ef4444;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const PanelContent = styled.div`
  flex: 1;
  overflow: hidden;
`;

// Panels mount lazily as they scroll into view — see LazyPanel. Every panel
// fetches on mount, so mounting all of them at once fired ~12 parallel requests
// before the dashboard was interactive. The first two are `eager` because they
// are above the fold on every screen size.

// Live AdviceStatusOverview (no blur - real data)
const LiveAdviceStatusOverview = (props) => (
    <LazyPanel eager><AdviceStatusOverview {...props} /></LazyPanel>
);

const LiveUpcomingSessions = (props) => (
    <LazyPanel eager><UpcomingSessions {...props} /></LazyPanel>
);

const BlurredEarningsSummary = (props) => (
    <LazyPanel><EarningsSummary {...props} /></LazyPanel>
);

// Live EngagementReach (no blur - real data)
const LiveEngagementReach = (props) => (
    <LazyPanel><EngagementReach {...props} /></LazyPanel>
);

// Expert Reviews component wrapper
const LiveExpertReviews = (props) => (
    <LazyPanel><ExpertReviews {...props} /></LazyPanel>
);

// PlatformNotifications is now a real component, no blur needed
const LivePlatformNotifications = (props) => (
    <LazyPanel><PlatformNotifications {...props} /></LazyPanel>
);

const SEODashboard = () => {
    const { getAccessTokenSilently } = useAuth0();
    const authenticatedAxios = useAuthenticatedAxios();
    const [calendarData, setCalendarData] = useState([]);
    const [cellAspectRatio, setCellAspectRatio] = useState(1.36);
    const [cellSpacing, setCellSpacing] = useState([7, 7]);
    const [padding, setPadding] = useState('8px');
    const [dashboardHeight, setDashboardHeight] = useState('100%');
    const [isAssistantOpen, setIsAssistantOpen] = useState(false);
    const [shouldRenderPanel, setShouldRenderPanel] = useState(false);

    // Handle assistant panel open/close with animation
    const toggleAssistant = () => {
        if (isAssistantOpen) {
            // Closing - wait for animation before removing from DOM
            setIsAssistantOpen(false);
            setTimeout(() => setShouldRenderPanel(false), 300);
        } else {
            // Opening - render immediately
            setShouldRenderPanel(true);
            setTimeout(() => setIsAssistantOpen(true), 10);
        }
    };

    useEffect(() => {
        const fetchCalendarData = async () => {
            try {
                const response = await authenticatedAxios.get('calander/GetCalanderData');
                setCalendarData(response.data);
            } catch (error) {
                console.log(error);
            }
        };

        fetchCalendarData();
        
        // Adjust layout based on screen size
        const updateLayoutParams = () => {
            if (window.innerWidth >= 2560) {
                setCellAspectRatio(1.0);
                setCellSpacing([8, 8]);
                setPadding('8px');
                setDashboardHeight('100%');
            } else if (window.innerWidth >= 1920) {
                setCellAspectRatio(0.95);
                setCellSpacing([8, 8]);
                setPadding('8px');
                setDashboardHeight('auto');
            } else if (window.innerWidth >= 1366) {
                setCellAspectRatio(0.9);
                setCellSpacing([7, 7]);
                setPadding('7px');
                setDashboardHeight('auto');
            } else {
                setCellAspectRatio(0.85);
                setCellSpacing([5, 5]);
                setPadding('5px');
                setDashboardHeight('auto');
            }
        };
        
        updateLayoutParams();
        window.addEventListener('resize', updateLayoutParams);
        
        return () => {
            window.removeEventListener('resize', updateLayoutParams);
        };
    }, [authenticatedAxios]);

    return (
        <DashboardAssistantProvider>
            <DashboardWrapper>
                <div id="analytic_target">
                    <div className="sidebar-content">
                        <div className="dashboardParent">
                            <DashboardLayoutComponent 
                                id="analytic_dashboard" 
                                cellAspectRatio={cellAspectRatio} 
                                cellSpacing={[9, 9]}
                                columns={6}
                                allowResizing={false}
                                draggableHandle=".e-panel-header"
                                mediaQuery={true}
                                className="e-dashboard-dark-theme"
                                style={{ 
                                    backgroundColor: 'transparent',
                                    width: '100%',
                                    margin: '0 auto',
                                    padding: '8px',
                                    height: dashboardHeight,
                                    minHeight: window.innerWidth <= 1920 ? '750px' : '850px',
                                    marginBottom: '10px'
                                }}
                            >
                                <PanelsDirective>
                                    {/* Panel 1: Case Status Overview - LIVE */}
                                    <PanelDirective 
                                        sizeX={2} 
                                        sizeY={2} 
                                        row={0} 
                                        col={0} 
                                        content={LiveAdviceStatusOverview} 
                                        header="<div>Case Status Overview</div>"
                                    ></PanelDirective>
                                    
                                    {/* Panel 2: Upcoming Sessions & Reminders - NOT BLURRED */}
                                    <PanelDirective 
                                        sizeX={2} 
                                        sizeY={2} 
                                        row={0} 
                                        col={2} 
                                        content={LiveUpcomingSessions}
                                        header="<div>Upcoming Sessions</div>"
                                    ></PanelDirective>
                                    
                                    {/* Panel 3: User Journey - LIVE */}
                                    <PanelDirective 
                                        sizeX={2} 
                                        sizeY={2} 
                                        row={0} 
                                        col={4} 
                                        content={LiveEngagementReach} 
                                        header="<div>User Journey</div>"
                                    ></PanelDirective>
                                    
                                    {/* Panel 4: Expert Reviews */}
                                    <PanelDirective
                                        sizeX={2}
                                        sizeY={2}
                                        row={2} 
                                        col={0}
                                        content={LiveExpertReviews} 
                                        header="<div>Client Reviews</div>"
                                    ></PanelDirective>
                                    
                                    {/* Panel 5: Earnings Summary - BLURRED */}
                                    <PanelDirective 
                                        sizeX={2} 
                                        sizeY={2} 
                                        row={2} 
                                        col={2} 
                                        content={BlurredEarningsSummary} 
                                        header="<div>Earnings Summary</div>"
                                    ></PanelDirective>
                                    
                                    {/* Panel 6: Platform Notifications - LIVE */}
                                    <PanelDirective 
                                        sizeX={2} 
                                        sizeY={2} 
                                        row={2} 
                                        col={4} 
                                        content={LivePlatformNotifications} 
                                        header="<div>Platform Notifications</div>"
                                    ></PanelDirective>
                                </PanelsDirective>
                            </DashboardLayoutComponent>
                        </div>
                    </div>
                </div>
                
                {/* Right Toolbar with Assistant Button */}
                <ToolbarContainer $isPanelOpen={isAssistantOpen}>
                    <ToolbarButton 
                        $active={isAssistantOpen}
                        onClick={toggleAssistant}
                        title="Dashboard Assistant"
                    >
                        <img src={HubAssistantIcon} alt="AI Assistant" />
                        <Tooltip>Dashboard Assistant</Tooltip>
                    </ToolbarButton>

                    {/* User-defined custom action buttons (+ their add/edit modal) */}
                    <CustomToolbarActions />
                </ToolbarContainer>
                
                {/* Assistant Panel */}
                {shouldRenderPanel && (
                    <AssistantPanel $isOpen={isAssistantOpen}>
                        <PanelHeader>
                            <div className="title-section">
                                <div className="icon">
                                    <img src={HubAssistantIcon} alt="" />
                                </div>
                                <div className="text">
                                    <h3>Dashboard Assistant</h3>
                                    <span>Ask about your dashboard</span>
                                </div>
                            </div>
                            <CloseButton onClick={toggleAssistant} title="Close">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M18 6L6 18M6 6l12 12" />
                                </svg>
                            </CloseButton>
                        </PanelHeader>
                        <PanelContent>
                            <DashboardAssistantPanel />
                        </PanelContent>
                    </AssistantPanel>
                )}
            </DashboardWrapper>
        </DashboardAssistantProvider>
    );
};

export default SEODashboard;
